import React, { useEffect, useState, useRef } from 'react';
import Particles, { initParticlesEngine } from '@tsparticles/react';
import { loadSlim } from '@tsparticles/slim';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaPhoneAlt, FaCrown } from 'react-icons/fa';

import AZ from '../../assets/logos/az.png';
import C1 from '../../assets/images/carteRectoX.png';
import C2 from '../../assets/images/VersoX.png';

// Palette white / gold / glass
const colors = {
  bg: '#FFFFFF',
  goldLight: '#F5E1C8',
  gold: '#C69C6D',
  goldDark: '#B88A56',
  text: '#3B2F2A',
  textSoft: '#5C4A3D',
  glassBg: 'rgba(255, 255, 255, 0.7)',
  glassBorder: 'rgba(198, 156, 109, 0.25)',
  shadow: '0 8px 24px rgba(107, 90, 70, 0.06)',
  shadowHover: '0 16px 32px rgba(184, 138, 86, 0.15)',
};

const CardAdhesion = () => {
  const [engineReady, setEngineReady] = useState(false);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, threshold: 0.2 });

  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadSlim(engine);
    }).then(() => setEngineReady(true));
  }, []);

  const particlesOptions = {
    background: { color: 'transparent' },
    fpsLimit: 60,
    particles: {
      number: { value: 60, density: { enable: true, area: 800 } },
      color: { value: ['#C69C6D', '#E2B88A', '#B88A56', '#D4B895'] }, // teintes dorées uniquement
      shape: { type: 'circle' },
      opacity: {
        value: { min: 0.2, max: 0.5 },
        anim: { enable: true, speed: 1, minimumValue: 0.1, sync: false }
      },
      size: {
        value: { min: 1, max: 4 },
        random: true,
        anim: { enable: true, speed: 2, minimumValue: 0.5, sync: false }
      },
      links: {
        enable: false, // pas de liens pour un rendu plus épuré
        distance: 140,
        color: '#C69C6D',
        opacity: 0.2,
        width: 1
      },
      move: {
        enable: true,
        speed: { min: 0.4, max: 1.2 },
        direction: 'none',
        random: true,
        outModes: 'bounce'
      }
    },
    interactivity: {
      events: {
        onHover: { enable: true, mode: 'bubble' },
        onClick: { enable: true, mode: 'push' }
      },
      modes: {
        bubble: { distance: 200, size: 8, duration: 2, opacity: 0.8 },
        grab: { distance: 150, links: { opacity: 0.5 } },
        push: { quantity: 3 }
      }
    },
    detectRetina: true
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
  };

  const cardImageVariants = {
    hidden: { opacity: 0, scale: 0.95, rotateY: 10 },
    visible: { opacity: 1, scale: 1, rotateY: 0, transition: { duration: 0.7, ease: 'easeOut' } },
    hover: { scale: 1.02, rotateY: 0, transition: { duration: 0.3 } }
  };

  return (
    <>
      <style>{`
        .fidelity-section {
          position: relative;
          background: ${colors.bg};
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 100px 20px;
          overflow: hidden;
        }

        /* Orbes dorées très légères */
        .fidelity-section::before,
        .fidelity-section::after {
          content: '';
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          opacity: 0.15;
          z-index: 0;
          pointer-events: none;
        }

        .fidelity-section::before {
          width: 45vw;
          height: 45vw;
          background: ${colors.goldLight};
          top: -10%;
          left: -5%;
          animation: floatOrb 18s ease-in-out infinite;
        }

        .fidelity-section::after {
          width: 35vw;
          height: 35vw;
          background: #EAD5C0;
          bottom: -5%;
          right: -5%;
          animation: floatOrb 15s ease-in-out infinite alternate-reverse;
        }

        @keyframes floatOrb {
          0% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(5%, 10%) scale(1.1); }
          100% { transform: translate(-5%, 5%) scale(0.95); }
        }

        .container {
          position: relative;
          z-index: 2;
          max-width: 1200px;
        }

        /* Particules canvas */
        #tsparticles {
          position: absolute;
          inset: 0;
          z-index: 1;
        }

        /* Titre */
        .section-title {
          font-size: clamp(2rem, 5vw, 2.8rem);
          font-weight: 700;
          color: ${colors.text};
          text-align: center;
          margin-bottom: 0.5rem;
          letter-spacing: -0.5px;
        }

        .title-underline {
          width: 80px;
          height: 3px;
          background: linear-gradient(90deg, ${colors.goldDark}, ${colors.gold});
          margin: 0 auto 40px;
          border-radius: 2px;
        }

        /* Logo flottant */
        .logo-wrapper {
          display: flex;
          justify-content: center;
          margin-bottom: 40px;
        }

        .logo-img {
          width: 220px;
          filter: drop-shadow(0 8px 16px rgba(0,0,0,0.05));
        }

        /* Cartes glass */
        .card-glass {
          background: ${colors.glassBg};
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid ${colors.glassBorder};
          border-radius: 24px;
          padding: 20px;
          box-shadow: ${colors.shadow};
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
        }

        .card-glass:hover {
          box-shadow: ${colors.shadowHover};
          border-color: ${colors.gold};
          transform: translateY(-4px);
        }

        .card-glass img {
          width: 100%;
          max-width: 450px;
          height: auto;
          border-radius: 15px;
          object-fit: contain;
        }

        /* Infos */
        .info-card {
          background: ${colors.glassBg};
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid ${colors.glassBorder};
          border-radius: 24px;
          padding: 30px;
          text-align: center;
          box-shadow: ${colors.shadow};
          color: ${colors.text};
        }

        .info-card h3 {
          color: ${colors.text};
          font-weight: 600;
          margin-bottom: 15px;
        }

        .info-card p {
          color: ${colors.textSoft};
          font-size: 1rem;
          line-height: 1.6;
          margin-bottom: 10px;
        }

        /* Bouton gold */
        .btn-gold {
          background: linear-gradient(135deg, ${colors.gold}, ${colors.goldDark});
          color: #fff;
          border: none;
          padding: 0.8rem 2.5rem;
          border-radius: 50px;
          font-weight: 600;
          font-size: 1rem;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s;
          box-shadow: 0 8px 20px rgba(198, 156, 109, 0.3);
        }

        .btn-gold:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 28px rgba(198, 156, 109, 0.4);
          color: #fff;
        }

        @media (max-width: 768px) {
          .fidelity-section {
            padding: 80px 16px;
          }
          .card-glass {
            padding: 12px;
          }
          .logo-img {
            width: 180px;
          }
        }
      `}</style>

      <section className="fidelity-section" ref={sectionRef}>
        {/* Particules dorées sur fond blanc */}
        {engineReady && (
          <Particles id="tsparticles" options={particlesOptions} />
        )}

        <div className="container">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            {/* Titre */}
            <motion.h1 className="section-title" variants={itemVariants}>
              CARTE DE FIDÉLITÉ
            </motion.h1>
            <motion.div className="title-underline" variants={itemVariants} />

            {/* Logo */}
            <motion.div className="logo-wrapper" variants={itemVariants}>
              <motion.img
                src={AZ}
                alt="Azyekos Logo"
                className="logo-img"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              />
            </motion.div>

            {/* Cartes */}
            <div className="row g-4 mb-5">
              <div className="col-md-6">
                <motion.div variants={cardImageVariants} whileHover="hover" className="card-glass">
                  <img src={C1} alt="Carte recto" />
                </motion.div>
              </div>
              <div className="col-md-6">
                <motion.div variants={cardImageVariants} whileHover="hover" className="card-glass">
                  <a href="tel:+243835593568" className="d-block">
                    <img src={C2} alt="Carte verso" />
                  </a>
                </motion.div>
              </div>
            </div>

            {/* Texte */}
            <motion.div variants={itemVariants} className="info-card">
              <h3>
                <FaCrown className="me-2" style={{ color: colors.gold }} />
                Un privilège réservé à nos habitués
              </h3>
              <p>Dès votre 10ᵉ visite, l'accès classique vous est offert.</p>
              <p className="fw-bold">
                Au 10ᵉ passage, l'accès classique (35 $) offert !
              </p>
              <p>
                Un grand merci à nos fidèles clients. Votre loyauté est notre plus belle récompense.
              </p>
            </motion.div>

            {/* Bouton */}
            <motion.div className="text-center mt-5" variants={itemVariants}>
              <Link to="/contact" className="btn-gold">
                <FaPhoneAlt /> Nous contacter
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default CardAdhesion;